import { GridCellParams, GridComparatorFn, gridDateComparator, GridValidRowModel } from '@mui/x-data-grid';
import { GridCurrencyCell } from '@components/gridCurrencyCell/GridCurrencyCell';
import { ColumnType, PolicySearchField, PolicyStatusText } from '@models/policy';
import { ReportingQuoteSourceLabels } from '@models/quotes';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';
import { MenuButton, MenuItemType } from '@components/menu';
import { formatDateToTimezone, getDateFormatter, LongDateFormatWithoutSeconds, ShortDateFormat } from '@utils/date';
import { GridLink } from '@components/gridLink/GridLink';
import { OverflowTooltip } from '@components/tooltip/OverflowTooltip';
import { Tooltip } from '@components/tooltip';
import { Typography } from '@mui/material';
import { EllipsisH } from '@assets/svgs';
import { TableGridBaseColDef } from '@components/table/types/TableColumnDef.type';
import { ClockFormat } from '@lio/rule-engine';
import { format } from 'date-fns-tz';
import { OpenSubjectivityColumn } from '@pages/Policies/policyDataGrid/columns/OpenSubjectivityColumn';
import { getDisplayedWritingCompanyLabel, getWritingCompanyTooltip } from '@utils/issuingWritingCompany';
import { getQuoteOriginLabel } from '@components/table/filters/QuoteOriginTypeaheadFilter';

const getApplyStringFilter =
  (value: string) =>
  (params: GridCellParams): boolean =>
    params.value?.toString().includes(value) || false;

const dateComparator: GridComparatorFn<string> = (v1, v2, cellParams1, cellParams2) =>
  gridDateComparator(new Date(v1).getTime(), new Date(v2).getTime(), cellParams1, cellParams2);

const disableComparator: GridComparatorFn<string> = () => {
  return 0;
};

type PolicyDataGridDef<T extends GridValidRowModel> = {
  isVisible?: ({ isInternalUser }: { isInternalUser: boolean }) => boolean;
} & TableGridBaseColDef<T>;

export function getColumnsBase({
  isInternalUser,
  userConfig,
}: {
  isInternalUser: boolean;
  userConfig: {
    layoutPreferences: {
      clockFormat: ClockFormat;
    };
  };
}): PolicyDataGridDef<any>[] {
  return [
    {
      field: PolicySearchField.OpenSubjectivities,
      type: ColumnType.Action,
      // these together will make the column 20px wide, overriding the default mui 50
      minWidth: 30,
      maxWidth: 30,
      renderCell: (params) => <OpenSubjectivityColumn openSubjectivities={params.row.subjectivities} />,
      mobileConfig: {
        hidden: {
          xs: true,
        },
      },
    },
    {
      field: PolicySearchField.PolicyId,
      headerName: 'Policy ID',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 100,
      getApplyQuickFilterFn: getApplyStringFilter,
      renderCell: (params) => <GridLink to={`/policy/${params.row.id}`}>{params.row.id}</GridLink>,
    },
    {
      mobileConfig: {
        mobileLockedColumn: true,
        mobileWidth: 150,
      },
      field: PolicySearchField.PolicyNumber,
      headerName: 'Policy Number',
      hideable: false,
      type: ColumnType.String,
      flex: 0.4,
      minWidth: 140,
      renderCell: (params) => (
        <GridLink
          textOverflow={
            getFormattedPolicyNumber(params.row.policyNumberPrefix, params.row.policyNumber.policyNumber) || '-'
          }
          to={`/policy/${params.row.id}`}
        >
          {getFormattedPolicyNumber(params.row.policyNumberPrefix, params.row.policyNumber.policyNumber) || '-'}
        </GridLink>
      ),
    },
    {
      field: PolicySearchField.Account,
      headerName: 'Account',
      type: ColumnType.String,
      flex: 1,
      minWidth: 200,
      renderCell: (params) =>
        isInternalUser ? (
          <GridLink textOverflow={params.row.customer.name} to={`/customer/${params.row.customer.id}`}>
            {params.row.customer.name || '-'}
          </GridLink>
        ) : (
          <OverflowTooltip>{params.row.customer.name || '-'}</OverflowTooltip>
        ),
    },
    {
      field: PolicySearchField.Product,
      headerName: 'Product',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 130,
      sortComparator: disableComparator,
      valueGetter: (value, row) => row.boundQuote?.offering?.insuranceProduct.name || '-',
    },
    {
      field: PolicySearchField.ProductLabel,
      headerName: 'Product Label',
      type: ColumnType.String,
      sortComparator: disableComparator,
      flex: 0.5,
      minWidth: 130,
      valueGetter: (value, row) => row.boundQuote?.productLabel?.name || '-',
    },
    {
      field: PolicySearchField.ProductOption,
      headerName: 'Business Class',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 130,
      valueGetter: (value, row) => {
        return row.boundQuote?.productOption?.name || '-';
      },
    },
    {
      field: PolicySearchField.PackageLabel,
      headerName: 'Line Of Business',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 130,
      valueGetter: (value, row) => {
        return row?.packageLabel || '-';
      },
    },
    {
      field: PolicySearchField.Status,
      headerName: 'Status',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 160,
      valueGetter: (value, row) => PolicyStatusText[row.status as keyof typeof PolicyStatusText] || '-',
    },
    {
      field: PolicySearchField.CurrentTerm,
      headerName: 'Active Term',
      flex: 0.5,
      minWidth: 180,
      sortComparator: dateComparator,
      getApplyQuickFilterFn: getApplyStringFilter,
      valueGetter: (value, row) => `${formatDateToTimezone(row.effectiveOn)} - ${formatDateToTimezone(row.expiresOn)}`,
    },
    {
      field: PolicySearchField.RenewalStatus,
      headerName: 'Renewal Status',
      flex: 0.5,
      minWidth: 120,
      sortable: false,
      isVisible: ({ isInternalUser }) => isInternalUser,
    },
    {
      field: PolicySearchField.QuoteSource,
      headerName: 'Quote Source',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 120,
      sortable: true,
      valueGetter: (value, row) =>
        row.reportingQuoteSource
          ? ReportingQuoteSourceLabels[row.reportingQuoteSource as keyof typeof ReportingQuoteSourceLabels]
          : '-',
      isVisible: ({ isInternalUser }) => isInternalUser,
    },
    {
      field: PolicySearchField.QuoteOrigin,
      headerName: 'Quote Origin',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 120,
      sortable: false,
      valueGetter: (value, row) => getQuoteOriginLabel(row.boundQuote?.quoteSource?.name),
      isVisible: ({ isInternalUser }) => isInternalUser,
    },
    {
      field: PolicySearchField.PolicyCost,
      headerName: 'Policy Cost',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 120,
      align: 'right',
      getApplyQuickFilterFn: getApplyStringFilter,
      sortComparator: disableComparator,
      renderCell: (params) =>
        params.row.policyCost !== null && params.row.policyCost > -1 ? (
          <GridCurrencyCell totalPremium={params.row.policyCost} />
        ) : (
          <Typography variant='bodySmall' sx={{ color: 'text.main' }}>
            -
          </Typography>
        ),
    },
    {
      field: PolicySearchField.Modified,
      headerName: 'Modified',
      flex: 0.5,
      minWidth: 110,
      getApplyQuickFilterFn: getApplyStringFilter,
      sortComparator: dateComparator,

      valueGetter: (value, row) => {
        return row.updatedAt
          ? format(
              new Date(row.updatedAt),
              getDateFormatter(userConfig.layoutPreferences.clockFormat, LongDateFormatWithoutSeconds),
            )
          : '-';
      },
    },
    {
      field: PolicySearchField.PendingBORTransfer,
      headerName: 'BOR Transfer',
      type: ColumnType.String,
      flex: 0.3,
      minWidth: 100,
      sortable: false,
      isVisible: ({ isInternalUser }) => isInternalUser,
      valueGetter: (value, row) => (row.pendingBORTransferRequestId ? 'Pending' : ''),
    },
    {
      field: PolicySearchField.ComplianceReviewDate,
      headerName: 'Compliance Review Date',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 160,
      sortable: false,
      isVisible: ({ isInternalUser }) => isInternalUser,
      valueGetter: (value, row) =>
        row.complianceReviewDate ? formatDateToTimezone(row.complianceReviewDate, ShortDateFormat) : '-',
    },
    {
      field: PolicySearchField.Brokerage,
      headerName: 'Brokerage',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 130,
      sortComparator: disableComparator,
      valueGetter: (value, row) => row.brokerage?.name ?? '-',
    },
    {
      field: PolicySearchField.Producer,
      headerName: 'Producer',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 130,
      sortComparator: disableComparator,
      valueGetter: (value, row) =>
        row.boundQuote?.brokerageProducer.user
          ? `${row.boundQuote.brokerageProducer.user.firstName} ${row.boundQuote.brokerageProducer.user.lastName}`
          : '-',
    },
    {
      field: PolicySearchField.Underwriter,
      headerName: 'Underwriter',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 130,
      sortComparator: disableComparator,
      valueGetter: (value, row) =>
        row.boundQuote?.underwriter.user
          ? `${row.boundQuote.underwriter.user.firstName} ${row.boundQuote.underwriter.user.lastName}`
          : '-',
    },
    {
      field: PolicySearchField.WritingCompany,
      headerName: 'Writing Company',
      type: ColumnType.String,
      flex: 0.5,
      minWidth: 130,
      // Sorting intentionally continues to key off the underlying writingCompanyId — an
      // overridden policy sorting among LIC rows while displaying as LSIC is intended, not a
      // bug. `boundQuote.issuingWritingCompany` is a nested relation (no scalar id on reads).
      sortComparator: disableComparator,
      valueGetter: (value, row) =>
        getDisplayedWritingCompanyLabel({
          writingCompanyName: row.boundQuote?.writingCompany?.name,
          issuingWritingCompanyId: row.boundQuote?.issuingWritingCompany?.id,
        }),
      renderCell: (params) => {
        const tooltip = getWritingCompanyTooltip(params.row.boundQuote?.issuingWritingCompany?.id);
        return tooltip ? (
          <Tooltip title={tooltip}>
            <Typography variant='bodySmall'>{params.value}</Typography>
          </Tooltip>
        ) : (
          <Typography variant='bodySmall'>{params.value}</Typography>
        );
      },
    },
    {
      headerName: '',
      field: 'config',
      flex: 1,
      align: 'right',
      sortable: false,
      valueGetter: () => undefined,
      renderCell: (params) => {
        return (
          <MenuButton
            icon={<EllipsisH />}
            items={[
              {
                type: MenuItemType.Link,
                actionName: `/policy/${params.row.id}`,
                text: 'View Details',
              },
            ]}
          />
        );
      },
    },
  ];
}

export const POLICIES_COLUMN_SELECTOR_KEY = 'policies';

export const columnSelector: Record<string, Record<string, string>> = {
  [POLICIES_COLUMN_SELECTOR_KEY]: {
    [PolicySearchField.PendingBORTransfer]: 'BOR Transfer',
    [PolicySearchField.PolicyId]: 'Policy ID',
    [PolicySearchField.PolicyNumber]: 'Policy Number',
    [PolicySearchField.Account]: 'Account',
    [PolicySearchField.Product]: 'Product',
    [PolicySearchField.ProductLabel]: 'Product Label',
    [PolicySearchField.ProductOption]: 'Business Class',
    [PolicySearchField.PackageLabel]: 'Line Of Business',
    [PolicySearchField.Status]: 'Status',
    [PolicySearchField.CurrentTerm]: 'Active Term',
    [PolicySearchField.RenewalStatus]: 'Renewal Status',
    [PolicySearchField.QuoteSource]: 'Quote Source',
    [PolicySearchField.QuoteOrigin]: 'Quote Origin',
    [PolicySearchField.PolicyCost]: 'Policy Cost',
    [PolicySearchField.Modified]: 'Modified',
    [PolicySearchField.ComplianceReviewDate]: 'Compliance Review Date',
    [PolicySearchField.Brokerage]: 'Brokerage',
    [PolicySearchField.Producer]: 'Producer',
    [PolicySearchField.Underwriter]: 'Underwriter',
    [PolicySearchField.WritingCompany]: 'Writing Company',
  },
};
